import * as Yup from 'yup';

// Mirrors util/passwordpolicy in auth-service. The server is the authority; this
// exists so the user is told what is wrong before submitting, and the two must be
// kept in step.
export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 72;

export const PASSWORD_REQUIREMENTS = [
  `At least ${MIN_PASSWORD_LENGTH} characters`,
  'An uppercase letter',
  'A lowercase letter',
  'A number',
  'A special character',
  'Not a commonly used or easily guessed password',
];

const COMMON_PASSWORDS = [
  'password', 'password1', 'password12', 'password123', 'password1234',
  'passw0rd', 'p@ssword', 'p@ssw0rd', 'qwerty', 'qwerty123', 'qwerty12345',
  'qwertyuiop', 'welcome', 'welcome1', 'welcome123', 'letmein', 'admin',
  'admin123', 'administrator', 'root', 'iloveyou', 'monkey', 'dragon',
  'sunshine', 'princess', 'football', 'baseball', 'trustno1', 'abc123',
  '123456', '1234567', '12345678', '123456789', '1234567890', '111111',
  '000000', 'changeme', 'secret', 'master', 'login', 'trademo', 'trademo123',
];

const SEQUENCES = ['abcdefg', 'qwertyu', 'asdfghj', 'zxcvbnm', '1234567', '7654321'];

const PADDING = /^[0-9!@#$%^&*()_+\-=.]+|[0-9!@#$%^&*()_+\-=.]+$/g;

const isCommon = (value) => {
  const lower = value.toLowerCase();
  if (COMMON_PASSWORDS.includes(lower)) return true;
  const trimmed = lower.replace(PADDING, '');
  return trimmed !== '' && COMMON_PASSWORDS.includes(trimmed);
};

const hasSequence = (value) => {
  const lower = value.toLowerCase();
  return SEQUENCES.some((seq) => lower.includes(seq));
};

const distinctCharacters = (value) => new Set(value).size;

/**
 * Yup schema for a password the user is setting. Pass the account email to also
 * reject a password built from it.
 */
export const passwordSchema = (email = '') =>
  Yup.string()
    .required('Password is required')
    .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
    .max(MAX_PASSWORD_LENGTH, `Password must be no more than ${MAX_PASSWORD_LENGTH} characters`)
    .matches(/[A-Z]/, 'Password must include an uppercase letter')
    .matches(/[a-z]/, 'Password must include a lowercase letter')
    .matches(/[0-9]/, 'Password must include a number')
    .matches(/[^A-Za-z0-9]/, 'Password must include a special character')
    .test('not-common', 'That is a commonly used password, please choose another', (value) => !value || !isCommon(value))
    .test('no-sequence', 'Password must not contain a keyboard or numeric sequence', (value) => !value || !hasSequence(value))
    .test('not-repetitive', 'Password must not repeat the same few characters', (value) => !value || distinctCharacters(value) >= 5)
    .test('not-email', 'Password must not be based on your email address', (value) => {
      if (!value || !email) return true;
      const lower = value.toLowerCase();
      const account = email.toLowerCase();
      if (lower === account) return false;
      const local = account.includes('@') ? account.split('@')[0] : account;
      if (local.length < 3) return true;
      return !lower.includes(local) && lower.replace(PADDING, '') !== local;
    });
